/**
 * Aranabilir seçim kutusu — web'deki `SearchableSelect` bileşeninin karşılığı.
 *
 * Tasarımcıdaki kutular iki bin beş yüzü aşan nesne listesi taşıyabiliyor; tarayıcının
 * kendi `<select>`inde aramak mümkün değil, listeyi gezmek gerekiyordu.
 *
 * Kutu kapalıyken seçili öğenin adını gösteren bir düğme; açıldığında arama kutusu ve
 * süzülmüş liste. Klavye: yukarı/aşağı gezinir, Enter seçer, Esc kapatır, yazmaya
 * başlamak süzer.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { cn } from '@/shared/lib/cn';
import { filterOptions, MAX_OPTIONS, type SearchOption } from '@/shared/ui/optionSearch';

interface Props {
  value: string | null | undefined;
  options: SearchOption[];
  onChange: (value: string | null) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function SearchSelect({ value, options, onChange, id, placeholder, disabled, className }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(() => options.find((o) => o.id === value) ?? null, [options, value]);
  const matched = useMemo(() => filterOptions(options, search), [options, search]);
  const shown = matched.slice(0, MAX_OPTIONS);

  // Arama değişince etkin satır başa döner; yoksa boş listede eski konum kalıyordu.
  useEffect(() => {
    setActive(0);
  }, [search]);

  // Etkin satır her zaman görünür olmalı (klavyeyle uzun listede gezinirken).
  useLayoutEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector('[data-active="true"]');
    // `scrollIntoView` her ortamda yok (jsdom, eski gömülü tarayıcılar); yokluğu
    // klavyeyle gezinmeyi çökertmemeli.
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const choose = (optionId: string | null) => {
    onChange(optionId);
    setOpen(false);
    setSearch('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return Math.max(0, Math.min(shown.length - 1, next));
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = shown[active];
      if (option) choose(option.id);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch('');
      }}
    >
      <div className={cn('relative', className)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            data-search-select
            className={cn(
              'border-border bg-bg focus-visible:ring-ring flex h-8 w-full items-center gap-1 rounded-md border pr-7 pl-2 text-left text-sm focus-visible:ring-2 focus-visible:outline-none',
              disabled && 'opacity-60',
            )}
          >
            <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-fg-subtle')}>
              {selected ? selected.name : (placeholder ?? '—')}
            </span>
            <ChevronDown className="text-fg-subtle pointer-events-none absolute right-2 h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>

        {/* Temizle düğmesi tetikleyicinin İÇİNDE değil: iç içe düğme geçersiz HTML. */}
        {selected && !disabled ? (
          <button
            type="button"
            onClick={() => choose(null)}
            aria-label={t('lab.select.clear')}
            className="text-fg-subtle hover:text-fg absolute top-1/2 right-6 -translate-y-1/2"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0">
        <div className="border-border relative border-b p-1.5">
          <Search className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
          {/* Radix açılışta ilk odaklanabilir öğeye odaklanır; o da bu kutu olsun. */}
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('lab.select.search')}
            aria-label={t('lab.select.search')}
            data-search-select-input
            className="border-border bg-bg focus-visible:ring-ring h-7 w-full rounded border pl-7 text-sm focus-visible:ring-1 focus-visible:outline-none"
          />
        </div>

        <div ref={listRef} className="max-h-72 overflow-auto p-1" role="listbox">
          {shown.length === 0 ? (
            <div className="text-fg-subtle px-2 py-3 text-center text-xs">
              {search ? t('lab.select.noMatch') : t('lab.select.empty')}
            </div>
          ) : (
            shown.map((option, index) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={option.id === value}
                data-active={index === active}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(option.id)}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm',
                  index === active && 'bg-bg-hover',
                )}
              >
                <Check
                  className={cn('h-3.5 w-3.5 shrink-0', option.id === value ? 'text-brand-500' : 'opacity-0')}
                />
                <span className="min-w-0 flex-1 truncate">{option.name}</span>
              </button>
            ))
          )}
          {matched.length > MAX_OPTIONS ? (
            <div className="text-fg-subtle px-2 py-1.5 text-center text-[11px]">
              {t('lab.select.more', { count: matched.length - MAX_OPTIONS })}
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
